import React from "react";
import {
  Box,
  IconButton,
  Badge,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  AddComment as AddCommentIcon,
  Share as ShareIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Info as InfoIcon,
  LocalOffer as LocalOfferIcon,
  Translate as TranslateIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  Message,
  Comment,
} from "../../../../redux/reducers/conversationsReducer";

interface MessageActionsProps {
  message: Message;
  comments: Comment[];
  showComments: boolean;
  setShowComments: (val: boolean) => void;
  canViewModelCalls?: boolean;
  handleShowLogsModal: () => void;
  handleShareMessage: () => void;
  showShareTooltip: boolean;
  setShowCommentModal: (val: boolean) => void;
  canViewAllData?: boolean;
  canReadConversation?: boolean; // Whether user has admin-level read access
  isRightAligned: boolean;
  conversationId?: string;
  chatSessionId: string;
  canViewMessageAudio?: boolean;
  needsTranslation?: boolean;
  showTranslation?: boolean;
  handleTranslateToggle?: () => void;
  translating?: boolean;
  hasRedactedContent?: boolean;
  showRedacted?: boolean;
  handleRedactionToggle?: () => void;
  showRedactionIndicator?: boolean; // Show greyed-out indicator in transcript view
}

const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  comments,
  showComments,
  setShowComments,
  canViewModelCalls,
  handleShowLogsModal,
  handleShareMessage,
  showShareTooltip,
  setShowCommentModal,
  canReadConversation,
  needsTranslation,
  showTranslation,
  handleTranslateToggle,
  translating,
  hasRedactedContent,
  showRedacted,
  handleRedactionToggle,
  showRedactionIndicator,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        mt: 1,
      }}
    >
      {/* Redaction toggle - functional for admins in real data view */}
      {canReadConversation && hasRedactedContent && (
        <Tooltip
          title={
            showRedacted
              ? "Show Original (PII visible)"
              : "Show Redacted (PII hidden)"
          }
          arrow
        >
          <IconButton
            size="small"
            onClick={handleRedactionToggle}
            aria-label={showRedacted ? "Show Original" : "Show Redacted"}
            sx={{
              color: showRedacted ? "#ed6c02" : "#2e7d32",
              "&:hover": {
                backgroundColor: showRedacted
                  ? "rgba(237, 108, 2, 0.08)"
                  : "rgba(46, 125, 50, 0.08)",
              },
            }}
          >
            {showRedacted ? (
              <VisibilityOffIcon style={{ fontSize: 20 }} />
            ) : (
              <VisibilityIcon style={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      {/* Redaction indicator - greyed out icon in transcript view when content has [REDACTED */}
      {showRedactionIndicator && (
        <Tooltip
          title="This message contains redacted content. Switch to Real Data view to toggle to original."
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled
              aria-label="Redacted content indicator"
            >
              <VisibilityOffIcon style={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* Translation toggle - only show if session needs translation */}
      {needsTranslation && message.content && (
        <Tooltip
          title={
            translating
              ? "Translating..."
              : showTranslation
              ? "Show Original"
              : "Translate to English"
          }
          arrow
        >
          <IconButton
            size="small"
            onClick={handleTranslateToggle}
            disabled={translating}
            aria-label={showTranslation ? "Show Original" : "Translate"}
            sx={{
              color: showTranslation ? "#1976d2" : "#000",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
            }}
          >
            {translating ? (
              <CircularProgress size={20} />
            ) : (
              <TranslateIcon style={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
      {/* Markup indicator - shows when markup exists */}
      {message.markup && (
        <Tooltip
          title={
            <Box
              sx={{
                maxWidth: 400,
                maxHeight: 300,
                overflow: "auto",
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
                fontSize: "0.875rem",
              }}
            >
              {message.markup}
            </Box>
          }
          placement="top"
          arrow
          enterDelay={200}
          leaveDelay={200}
          sx={{
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#424242",
              color: "#fff",
              fontSize: "0.875rem",
              maxWidth: "none",
              padding: "12px",
            },
            "& .MuiTooltip-arrow": {
              color: "#424242",
            },
          }}
        >
          <IconButton
            size="small"
            aria-label="View Markup"
            sx={{
              color: "#000",
              "&:hover": {
                backgroundColor: "rgba(156, 39, 176, 0.08)",
              },
            }}
          >
            <LocalOfferIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        size="small"
        onClick={() => setShowCommentModal(true)}
        aria-label="Add Comment"
      >
        <AddCommentIcon style={{ color: "black" }} />
      </IconButton>
      {canViewModelCalls && (
        <IconButton
          size="small"
          onClick={() => {
            handleShowLogsModal();
          }}
          aria-label="Show Logs"
        >
          <InfoIcon style={{ color: "black" }} />
        </IconButton>
      )}
      {comments.length > 0 && (
        <IconButton
          size="small"
          onClick={() => setShowComments(!showComments)}
          aria-label="Toggle Comments"
        >
          <Badge badgeContent={comments.length} color="info" overlap="circular">
            <QuestionAnswerIcon style={{ color: "black" }} />
          </Badge>
        </IconButton>
      )}
      <Tooltip
        title={showShareTooltip ? "Copied to clipboard" : "Share message"}
        open={showShareTooltip}
      >
        <IconButton size="small" onClick={handleShareMessage}>
          <ShareIcon style={{ color: "black" }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MessageActions;
